import type { Certification } from 'src/types/Certification';
import type {
  FormSignatureRequestType,
  IndividualSignatureRequestType,
} from 'src/types/SignatureTypes';
import { certKindLabel } from '../utils/certTerminology';

export interface RenterSignatureDoc {
  fsrId: number;
  certificationId: number;
  docId: number;
  certDisplayName: string;
  certCategory: string;
  propertyName: string;
  documentTitle: string;
  pendingCount: number;
  totalUserSigs: number;
  effectiveDate: string | null;
}

export interface SignedGroup {
  propertyName: string;
  certCategory: string;
  effectiveYear: string;
  docs: RenterSignatureDoc[];
}

export function isUserTenantSignature(
  sig: IndividualSignatureRequestType,
  userId: number
): boolean {
  return (
    !sig.is_date &&
    !sig.is_name &&
    !sig.is_hhmt &&
    !sig.is_phone &&
    !sig.compliance &&
    sig.user_id === userId
  );
}

function getUserSigsForFsr(
  fsr: FormSignatureRequestType,
  userId: number
): IndividualSignatureRequestType[] {
  return (fsr.needed_signatures ?? []).filter((sig) =>
    isUserTenantSignature(sig, userId)
  );
}

function buildDocEntry(
  fsr: FormSignatureRequestType,
  cert: Certification,
  userSigs: IndividualSignatureRequestType[]
): RenterSignatureDoc {
  return {
    fsrId: fsr.id,
    certificationId: cert.id,
    docId: fsr.certification_document,
    certDisplayName: cert.display_name,
    certCategory: certKindLabel(cert, { capitalize: true }),
    propertyName: cert.property?.name ?? '',
    documentTitle:
      fsr.document_title || `Document #${fsr.certification_document}`,
    pendingCount: userSigs.filter((s) => !s.signature_completed).length,
    totalUserSigs: userSigs.length,
    effectiveDate: cert.effective_date ?? null,
  };
}

export function getRequestedDocs(
  certifications: Certification[],
  userId: number
): RenterSignatureDoc[] {
  const docs: RenterSignatureDoc[] = [];
  for (const cert of certifications) {
    for (const fsr of cert.form_signatures_request ?? []) {
      if (!fsr.requested) {
        continue;
      }
      const userSigs = getUserSigsForFsr(fsr, userId);
      const pending = userSigs.filter((s) => !s.signature_completed);
      if (pending.length > 0) {
        docs.push(buildDocEntry(fsr, cert, userSigs));
      }
    }
  }
  return docs;
}

export function getSignedByGroup(
  certifications: Certification[],
  userId: number
): Record<string, SignedGroup> {
  const groups: Record<string, SignedGroup> = {};
  for (const cert of certifications) {
    for (const fsr of cert.form_signatures_request ?? []) {
      if (!fsr.requested) {
        continue;
      }
      const userSigs = getUserSigsForFsr(fsr, userId);
      if (userSigs.length === 0) {
        continue;
      }
      const allSigned = userSigs.every((s) => s.signature_completed);
      if (!allSigned) {
        continue;
      }
      const propertyName = cert.property?.name ?? '';
      const effectiveYear = cert.effective_date
        ? new Date(cert.effective_date).getFullYear().toString()
        : '';
      const certCategory = certKindLabel(cert, { capitalize: true });
      const groupKey = `${propertyName}|${certCategory}|${effectiveYear}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          propertyName,
          certCategory,
          effectiveYear,
          docs: [],
        };
      }
      groups[groupKey].docs.push(buildDocEntry(fsr, cert, userSigs));
    }
  }
  return groups;
}
